import { RefundSummary } from '../process-island/summary-tab/refund-summary'
import { formatCurrency } from '@/utils'
import { Button, TextInput, Base } from '@itsrever/design-system'
import { useState } from 'react'
import Edit from '@/assets/images/icons/Edit.svg'
import Success from '@/assets/images/icons/Success.svg'
import { useTranslation } from 'react-i18next'
import { useSubmitAuthorization } from '@/hooks/use-submit-authorization'
import { useParams } from 'react-router'
import { useAppSelector } from '@/redux/hooks'
import { PbECommerceSettingsUseCaseEnum } from '@itsrever/rever-api'
import { Compensation } from '@/domain/refund'
import { ReturnProcess } from '@/domain/returns'
import { buildRefundOverride } from '../refund-amount/build-refund-override'
import { useRefundAmountEditor } from '../refund-amount/use-refund-amount-editor'

const { Card } = Base

interface AuthorizeIslandProps {
  processDetails?: ReturnProcess
  setAuthorizeMode: (value: boolean) => void
  editAmount: boolean
  setEditAmount: (value: boolean) => void
}

const AuthorizeIsland: React.FC<AuthorizeIslandProps> = ({
  processDetails,
  setAuthorizeMode,
  editAmount
}) => {
  const { t } = useTranslation()
  const {
    mutate: submitAuthorization,
    isPending: loadingAuthorization,
    isSuccess: authorizationSuccess
  } = useSubmitAuthorization()
  const user = useAppSelector(
    (store) => store.userApi.getMe.response.user?.name
  )
  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )
  const isReverLite = settings.use_case === PbECommerceSettingsUseCaseEnum.Lite
  const { id } = useParams()
  const [reason, setReason] = useState('')
  const [disabled, setDisabled] = useState(!editAmount)

  const returnedItems = processDetails?.returnedItems ?? []
  const exchangeItems = processDetails?.orderedItems ?? []
  const extraLineItems = processDetails?.extraLineItems ?? []

  const userSelectedPM =
    processDetails?.refundPaymentMethod ?? 'NO_REFUND_METHOD'

  const compensation = processDetails?.compensation ?? ({} as Compensation)

  const {
    currency,
    calculatedRefundAmount,
    refundAmount,
    setRefundAmount,
    amountEdited,
    error,
    hint
  } = useRefundAmountEditor({
    compensation,
    maximumRefundAmount: processDetails?.maximumRefundAmount.amount,
    isReverLite
  })
  // Lite takes an edited amount without a reason.
  const needsReason = amountEdited && !isReverLite
  // An unedited prefill is never sent, so only an edited amount must be valid.
  const cannotSend = amountEdited && error

  const authorizeProcess = () => {
    submitAuthorization({
      authorized: true,
      process_id: id,
      user: user,
      refund_override: buildRefundOverride(
        amountEdited,
        reason,
        refundAmount,
        currency
      )
    })
  }

  const handleClickCancel = () => {
    setAuthorizeMode(false)
  }

  return (
    <Card className="h-fit p-5">
      <RefundSummary
        returnedItems={returnedItems}
        exchangeItems={exchangeItems}
        extraLineItems={extraLineItems}
        compensation={compensation}
        userSelectedPM={userSelectedPM}
      />
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm">
          {t('refund_island.refund_amount')} ({currency})
        </p>
        {disabled ? (
          <div
            className="flex cursor-pointer items-center text-grey-3 hover:text-grey-1"
            onClick={() => setDisabled(false)}
          >
            <img src={Edit} className="size-[20px]" />
            <span className="text-xs">{t('refund_island.edit')}</span>
          </div>
        ) : null}
      </div>
      <TextInput
        fullWidth
        hint={hint}
        placeholder={formatCurrency(
          calculatedRefundAmount.toFixed(2),
          currency
        )}
        value={refundAmount}
        onChange={(e) => setRefundAmount(e.currentTarget.value)}
        error={error}
        disabled={disabled}
      />
      {!disabled && needsReason && (
        <div className="mt-6">
          <p className="text-sm">{t('refund_island.reason_for_edit')}</p>
          <TextInput
            fullWidth
            value={reason}
            placeholder='e.g. "Box was damaged"'
            onChange={(e) => setReason(e.currentTarget.value)}
          />
        </div>
      )}
      <div className="mt-4 flex justify-center gap-4">
        {loadingAuthorization ? (
          <Base.Spinner label={t('process_details_page.authorizing')} />
        ) : authorizationSuccess ? (
          <div className="flex items-center rounded-[5rem] border border-solid border-[rgb(85,_181,_118)] py-2 pl-3 pr-4 text-sm text-[rgb(85,_181,_118)]">
            <img
              className="mr-1 size-6 text-[rgb(85,_181,_118)]"
              src={Success}
            />
            Success
          </div>
        ) : (
          <>
            <Button variant="outlined" onClick={handleClickCancel}>
              {t('products_tab.cancel')}
            </Button>
            <div>
              <Button
                onClick={authorizeProcess}
                disabled={cannotSend || (needsReason && reason.length < 3)}
              >
                {t('process_details_page.authorize_button')}{' '}
                {formatCurrency(refundAmount, currency)}
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  )
}

export default AuthorizeIsland
